/**
 * Hero background video (Figma 48:1690 desktop / 622:3077 mobile — the layer both home
 * frames put behind the copy is a still of this clip). Muted, looping, full-bleed, no
 * controls: neither frame draws a play glyph, so this is wallpaper and not the boxed
 * click-to-play player `filliers-video` builds.
 *
 * Why play() lives here instead of an `autoplay` attribute in the Liquid: the section has
 * to honour `prefers-reduced-motion`, and a CSS media query cannot stop a video. With
 * `autoplay` in the markup the browser starts painting frames before this deferred script
 * ever runs, so a reduced-motion visitor would see exactly the motion we promised to
 * withhold — a `pause()` after the fact is a flash, not a fix. Withholding the attribute
 * and gating play() is the only version that cannot flash. The cost is that a visitor with
 * no JS never gets motion; they get the poster, which is the designed fallback anyway.
 *
 * The query is read once in connectedCallback, the same one-shot check filliers-visual.js
 * makes — someone who flips the OS setting mid-page picks it up on the next navigation. No
 * IntersectionObserver either, for the reason that file records (unreliable in preview
 * contexts) and because a hero is above the fold by definition.
 */
class FilliersHeroVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    // The stylesheet already hides the element under this query, so bailing here leaves a
    // reduced-motion visitor with the section's <picture> and nothing to pause.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Belt and braces over the Liquid attributes: no browser grants autoplay to a video
    // with sound, and without playsinline iOS replaces the page with its own fullscreen
    // player. Setting the properties is immune to whatever `video_tag` chooses to emit.
    this.video.muted = true;
    this.video.playsInline = true;

    // A file that will not decode never rejects play() — it errors afterwards.
    this.video.addEventListener('error', () => this.fallBackToPoster(), { once: true });

    const started = this.video.play();
    if (started && typeof started.catch === 'function') {
      started.catch(() => this.fallBackToPoster());
    }
  }

  /**
   * Autoplay refused (Low Power Mode, a data saver, a browser policy) or the file broke.
   * Dropping the video out of the stack uncovers the section's <picture>: the video's own
   * poster would hold the frame too, but that is one flat file at one crop, where the
   * picture is the desktop/mobile art direction the editor actually set.
   */
  fallBackToPoster() {
    this.dataset.state = 'failed';
  }
}

if (!customElements.get('filliers-hero-video')) {
  customElements.define('filliers-hero-video', FilliersHeroVideo);
}
